import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { toast } from '../../../../Components/Toast';
import { createUserLotList } from '../../../../services/userLotsService';
import {
  SPACE_GUEST_EMPTY_LISTS_TITLE,
  resolveSpaceGuestWriteError,
} from '../../../utils/spaceMirrorLots';
import { colors, typography } from '../../../../styles/theme';
import {
  mapUserLotListsToSelectOptions,
  renderUserLotListSelectValue,
} from '../../../utils/userLotListSelectUtils';

const DOCK_LIST_SELECT_HEIGHT = 36;
const VIEWPORT_PADDING = 12;
const MENU_CHROME_HEIGHT = 140;
const MENU_LIST_MIN_HEIGHT = 120;
const MENU_MAX_HEIGHT = 240;

const POPOVER_POPPER_SLOT_PROPS = {
  popper: {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          boundary: 'viewport',
          padding: VIEWPORT_PADDING,
          altAxis: true,
        },
      },
      { name: 'flip', enabled: true },
    ],
  },
};

const getAvailableMenuListHeight = (anchorEl, preferredListHeight) => {
  if (!anchorEl || typeof window === 'undefined') return preferredListHeight;

  const rect = anchorEl.getBoundingClientRect();
  const placementGap = 8;
  const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING - placementGap - MENU_CHROME_HEIGHT;
  const spaceAbove = rect.top - VIEWPORT_PADDING - placementGap - MENU_CHROME_HEIGHT;
  const available = Math.max(spaceBelow, spaceAbove);

  return Math.max(
    MENU_LIST_MIN_HEIGHT,
    Math.min(preferredListHeight, Math.floor(available)),
  );
};

const checkboxSx = {
  p: 0.5,
  mr: 1,
  color: 'rgba(255,255,255,0.35)',
  '&.Mui-checked': { color: colors.primary },
};

const optionRowSx = (selected) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 0,
  px: 1.5,
  py: 0.875,
  cursor: 'pointer',
  bgcolor: selected ? 'rgba(72,146,145,0.12)' : 'transparent',
  '&:hover': { bgcolor: selected ? 'rgba(72,146,145,0.16)' : 'rgba(255,255,255,0.06)' },
});

const normalizeCreatedList = (data) => {
  if (!data || typeof data !== 'object') return null;
  const id = data.id ?? data.list_id;
  if (!id) return null;
  return {
    id,
    name: String(data.name || '').trim() || 'Sin nombre',
    is_public: Boolean(data.is_public),
    lot_count: data.lot_count ?? 0,
  };
};

const UserLotListMultiSelect = ({
  userLotLists = [],
  value = [],
  onChange,
  onListCreated,
  disabled = false,
  id = 'user-lot-list-select',
  // Espacio ajeno (F2): la lista nueva nace en el espacio del owner.
  ownerUserId = null,
}) => {
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const createInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [resolvedListMaxHeight, setResolvedListMaxHeight] = useState(MENU_MAX_HEIGHT);
  const [search, setSearch] = useState('');
  const [createMode, setCreateMode] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdLists, setCreatedLists] = useState([]);

  const mergedUserLotLists = useMemo(() => {
    const seen = new Set();
    const rows = [];
    [...userLotLists, ...createdLists].forEach((row) => {
      if (!row?.id || seen.has(row.id)) return;
      seen.add(row.id);
      rows.push(row);
    });
    return rows;
  }, [userLotLists, createdLists]);

  const options = useMemo(
    () => mapUserLotListsToSelectOptions(mergedUserLotLists),
    [mergedUserLotLists],
  );

  const selected = useMemo(
    () => (Array.isArray(value) ? value.filter(Boolean) : []),
    [value],
  );

  const displayLabel = useMemo(
    () => renderUserLotListSelectValue(selected, options),
    [selected, options],
  );

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => String(o.label || '').toLowerCase().includes(term));
  }, [options, search]);

  const toggleOption = (optionValue) => {
    onChange?.(
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };

  const resetCreateState = () => {
    setCreateMode(false);
    setNewListName('');
    setCreating(false);
  };

  const handleOpen = () => {
    if (disabled) return;
    const anchorEl = triggerRef.current;
    setMenuWidth(anchorEl?.offsetWidth ?? null);
    setResolvedListMaxHeight(getAvailableMenuListHeight(anchorEl, MENU_MAX_HEIGHT));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearch('');
    resetCreateState();
  };

  const handleCreateList = async () => {
    const name = newListName.trim();
    if (!name || creating) return;

    setCreating(true);
    try {
      const res = await createUserLotList({ name, is_public: false, ownerUserId });
      const created = normalizeCreatedList(res.data);
      if (!created) {
        toast.error({
          title: 'Error',
          message: resolveSpaceGuestWriteError(res._httpStatus, ownerUserId)
            || res.error || res.message || 'No se pudo crear la lista.',
        });
        return;
      }

      setCreatedLists((prev) => [...prev, created]);
      onListCreated?.(created);
      onChange?.(selected.includes(created.id) ? selected : [...selected, created.id]);
      toast.success('Lista creada con éxito.');
      resetCreateState();
    } catch (error) {
      console.error('Error creating user lot list:', error);
      toast.error({ title: 'Error', message: 'No se pudo crear la lista.' });
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (!open || createMode) return undefined;
    const t = setTimeout(() => searchRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open, createMode]);

  useEffect(() => {
    if (!open || !createMode) return undefined;
    const t = setTimeout(() => createInputRef.current?.focus(), 40);
    return () => clearTimeout(t);
  }, [open, createMode]);

  useEffect(() => {
    if (!open) return undefined;

    const syncMenuHeight = () => {
      setResolvedListMaxHeight(
        getAvailableMenuListHeight(triggerRef.current, MENU_MAX_HEIGHT),
      );
    };

    window.addEventListener('resize', syncMenuHeight);
    window.addEventListener('scroll', syncMenuHeight, true);
    return () => {
      window.removeEventListener('resize', syncMenuHeight);
      window.removeEventListener('scroll', syncMenuHeight, true);
    };
  }, [open]);

  const hasSelection = selected.length > 0;
  const canSubmitCreate = Boolean(newListName.trim()) && !creating;
  const trimmedCreateName = newListName.trim();

  return (
    <Box ref={triggerRef} sx={{ position: 'relative', minWidth: 0, width: '100%' }}>
      <Box
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-disabled={disabled}
        onClick={handleOpen}
        sx={{
          height: DOCK_LIST_SELECT_HEIGHT,
          minHeight: DOCK_LIST_SELECT_HEIGHT,
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          backgroundColor: '#242424',
          borderRadius: '8px',
          border: `1px solid ${open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.12)'}`,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          userSelect: 'none',
          transition: 'border-color 0.15s, background 0.15s',
          '&:hover': disabled ? {} : {
            borderColor: open ? 'rgba(255,255,255,0.28)' : 'rgba(255,255,255,0.20)',
          },
        }}
      >
        <Typography
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            fontFamily: typography.fontFamily.primary,
            fontSize: '13px',
            color: hasSelection ? '#ffffff' : 'rgba(255,255,255,0.55)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {displayLabel}
        </Typography>
        <ExpandMoreIcon
          sx={{
            fontSize: 18,
            color: 'rgba(255,255,255,0.65)',
            flexShrink: 0,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
          }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={triggerRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        marginThreshold={VIEWPORT_PADDING}
        slotProps={{
          ...POPOVER_POPPER_SLOT_PROPS,
          paper: {
            sx: {
              mt: 0.75,
              bgcolor: colors.backgroundSurface,
              color: colors.textPrimary,
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '10px',
              overflow: 'hidden',
              boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: `calc(100vh - ${VIEWPORT_PADDING * 2}px)`,
              ...(menuWidth
                ? { width: menuWidth, minWidth: menuWidth, maxWidth: menuWidth }
                : {}),
            },
          },
        }}
      >
        {createMode ? (
          <Box
            sx={{
              p: '8px 10px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              component="input"
              ref={createInputRef}
              type="text"
              placeholder="Nombre de la lista"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter' && canSubmitCreate) {
                  e.preventDefault();
                  handleCreateList();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  resetCreateState();
                }
              }}
              disabled={creating}
              sx={{
                flex: 1,
                minWidth: 0,
                background: 'rgba(255,255,255,0.07)',
                border: `1px solid ${trimmedCreateName ? colors.primary : 'rgba(255,255,255,0.10)'}`,
                borderRadius: '6px',
                color: '#fff',
                fontSize: '12px',
                padding: '5px 8px',
                outline: 'none',
                fontFamily: typography.fontFamily.primary,
                boxSizing: 'border-box',
                '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                '&:focus': { borderColor: colors.primary },
                '&:disabled': { opacity: 0.6 },
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={handleCreateList}
              disabled={!canSubmitCreate}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                flexShrink: 0,
                background: 'none',
                border: 'none',
                p: 0,
                cursor: canSubmitCreate ? 'pointer' : 'default',
                color: canSubmitCreate ? colors.primary : 'rgba(255,255,255,0.28)',
                fontFamily: typography.fontFamily.primary,
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:hover': canSubmitCreate ? { color: colors.primaryLight } : undefined,
              }}
            >
              {creating ? (
                <CircularProgress size={14} sx={{ color: colors.primary }} />
              ) : (
                <AddCircleOutlineIcon sx={{ fontSize: 16 }} />
              )}
              Crear
            </Box>
          </Box>
        ) : (
          <Box
            sx={{ p: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.07)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box
              component="input"
              ref={searchRef}
              type="text"
              placeholder="Buscar lista..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              sx={{
                width: '100%',
                background: 'rgba(255,255,255,0.07)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: '6px',
                color: '#fff',
                fontSize: '12px',
                padding: '5px 8px',
                outline: 'none',
                fontFamily: typography.fontFamily.primary,
                boxSizing: 'border-box',
                '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                '&:focus': { borderColor: 'rgba(255,255,255,0.22)' },
              }}
            />
          </Box>
        )}

        <Box
          role="listbox"
          aria-multiselectable="true"
          sx={{
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: resolvedListMaxHeight,
            overflowY: 'auto',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(255,255,255,0.15) transparent',
            '&::-webkit-scrollbar': { width: 5 },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 99,
            },
            py: '4px',
          }}
        >
          {!createMode && (
            <Box
              role="button"
              tabIndex={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.875,
                cursor: 'pointer',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
              onClick={() => setCreateMode(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setCreateMode(true);
                }
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 16, color: colors.primary }} />
              <Typography sx={{ fontSize: '13px', color: colors.primary, fontWeight: 500 }}>
                Crear nueva lista
              </Typography>
            </Box>
          )}

          {filteredOptions.length === 0 ? (
            <Typography sx={{ px: 1.5, py: 1.25, fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>
              {search.trim()
                ? 'Sin listas que coincidan'
                : (ownerUserId ? SPACE_GUEST_EMPTY_LISTS_TITLE : 'Todavía no tenés listas')}
            </Typography>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selected.includes(option.value);
              return (
                <Box
                  key={option.value}
                  role="option"
                  aria-selected={isSelected}
                  sx={optionRowSx(isSelected)}
                  onClick={() => toggleOption(option.value)}
                >
                  <Checkbox size="small" checked={isSelected} tabIndex={-1} sx={checkboxSx} />
                  <ListItemText
                    primary={option.label}
                    primaryTypographyProps={{
                      fontSize: '13px',
                      color: '#ffffff',
                      noWrap: true,
                    }}
                    sx={{ minWidth: 0, m: 0 }}
                  />
                </Box>
              );
            })
          )}
        </Box>

        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderTop: '1px solid rgba(255,255,255,0.07)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)' }}>
            {options.length} opción{options.length !== 1 ? 'es' : ''}
          </Typography>
          {hasSelection && (
            <Box
              component="button"
              type="button"
              onClick={() => onChange?.([])}
              sx={{
                fontSize: '12px',
                color: 'rgba(255,255,255,0.4)',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                p: 0,
                fontFamily: typography.fontFamily.primary,
                '&:hover': { color: 'rgba(255,255,255,0.75)' },
              }}
            >
              Limpiar selección
            </Box>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

export default UserLotListMultiSelect;
